import React, { useState, useEffect } from 'react';
import { CheckIcon, XMarkIcon, ShieldCheckIcon, BoltIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { PricingTable } from '@clerk/clerk-react';
import { exchangeRateService } from '@/services/exchangeRateService';
import { usePlanes } from '@/hooks/usePlanes';
import { formatClp, formatUsd, getPlanPricing } from '@/utils/currencyUtils';

// CSS simple para ajustar footer del modal de Clerk en móvil
const globalClerkModalStyles = `
  /* Solo ajustar footer en móvil para que no lo cubra el sidebar */
  @media (max-width: 768px) {
    .cl-drawerFooter,
    .cl-internal-71m5fh,
    [class*="cl-internal-"][class*="footer"] {
      padding-bottom: 80px !important; /* Espacio para el sidebar móvil */
    }
  }
`;

const PricingPage = () => {
  const [exchangeRateInfo, setExchangeRateInfo] = useState({
    rate: 950,
    source: 'Cargando...',
    formatted: '950'
  });
  const [selectedPeriod, setSelectedPeriod] = useState('mensual');
  // Obtener planes desde la base de datos
  const { planes, loading: planesLoading, error: planesError } = usePlanes();

  // Inyectar estilos globales para el modal de Clerk
  useEffect(() => {
    const styleId = 'clerk-modal-z-index-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = globalClerkModalStyles;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Cargar tasa de cambio
  useEffect(() => {
    const loadExchangeRate = async () => {
      try {
        const info = await exchangeRateService.getExchangeRateInfo();
        setExchangeRateInfo(info);
      } catch (error) {
        console.error('Error cargando tasa de cambio:', error);
        setExchangeRateInfo({
          rate: 950,
          source: 'Valor por defecto',
          formatted: '950',
          error: error.message
        });
      }
    };
    
    loadExchangeRate();
  }, []);

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold">Elige el Plan Perfecto</h1>
        </div>
        <p className="text-base text-gray-300 mb-6">
          Encuentra el plan que mejor se adapte a las necesidades de tu taller. 
          Puedes cambiar o cancelar en cualquier momento.
        </p>

        {/* Tabla comparativa de características - solo planes de pago */}
        {!planesLoading && !planesError && planes.length > 0 && (
            <div className="relative">
              {/* Header con Características y PricingTable - Responsive */}
              <div className="overflow-hidden">
                {/* Desktop: Header en la misma fila */}
                <div className="hidden lg:grid gap-4 mb-8 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors" style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                  {/* Columna de Características */}
                  <div className="flex items-center justify-left h-full">
                    <h4 className="font-medium text-white">
                      Características / Planes
                    </h4>
                  </div>
                  
                  {/* PricingTable ocupando las 3 columnas restantes */}
                  <div className="col-span-3">
                    <PricingTable 
                      appearance={{
                        baseTheme: 'dark',
                        elements: {
                          rootBox: 'bg-transparent',
                          card: 'bg-gray-800/50 border-gray-700/50 rounded-xl',
                          cardBox: 'shadow-lg',
                          planName: 'text-white font-bold text-xl',
                          planDescription: 'text-gray-400',
                          planPrice: 'text-white font-extrabold text-3xl',
                          planFeatureList: 'text-gray-300',
                          planFeatureText: 'text-gray-300',
                          formButtonPrimary: 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 rounded-xl font-semibold py-3 px-6 transition-all duration-200',
                          badge: 'bg-blue-500 text-white rounded-full px-3 py-1 text-xs font-bold'
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Mobile: Stack verticalmente */}
                <div className="lg:hidden space-y-6 mb-8">
                  {/* PricingTable para móvil */}
                  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/30">
                    <PricingTable 
                      appearance={{
                        baseTheme: 'dark',
                        elements: {
                          rootBox: 'bg-transparent',
                          card: 'bg-gray-800/50 border-gray-700/50 rounded-xl',
                          cardBox: 'shadow-lg',
                          planName: 'text-white font-bold text-lg',
                          planDescription: 'text-gray-400 text-sm',
                          planPrice: 'text-white font-extrabold text-2xl',
                          planFeatureList: 'text-gray-300 text-sm',
                          planFeatureText: 'text-gray-300 text-sm',
                          formButtonPrimary: 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 rounded-xl font-semibold py-2 px-4 transition-all duration-200 text-sm',
                          badge: 'bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-bold'
                        }
                      }}
                    />
                  </div>
                  
                  {/* Header de Características para móvil */}
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-white mb-4">
                      Comparación de Características
                    </h4>
                  </div>
                </div>
              
                {/* Filas de comparación - Responsive */}
                <div className="space-y-3">
                  {/* Precio CLP */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors" 
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2 flex flex-col justify-center h-full">
                      <div className="flex items-center gap-3">
                        <span className="whitespace-nowrap">Precio (**)</span>
                        <div className="inline-flex items-center bg-gray-700 rounded-full p-0.5 text-xs">
                          <button
                            onClick={() => setSelectedPeriod('mensual')}
                            className={`px-3 py-1 rounded-full font-medium transition-colors ${
                              selectedPeriod === 'mensual'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Mensual
                          </button>
                          <button
                            onClick={() => setSelectedPeriod('anual')}
                            className={`px-3 py-1 rounded-full font-medium transition-colors ${
                              selectedPeriod === 'anual'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Anual (-20%)
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 ml-16">
                        1 USD = ${exchangeRateInfo.formatted} CLP (*)
                      </div>
                    </div>
                    {planes.filter(plan => plan.price > 0).map((plan) => {
                      const pricing = getPlanPricing(plan, selectedPeriod);
                      const monthlyPricing = getPlanPricing(plan, 'mensual');
                      
                      return (
                        <div key={plan.id} className="text-center text-white font-semibold px-2">
                          {selectedPeriod === 'anual' ? (
                            <>
                              <div className="text-2xl font-bold text-green-400">
                                {formatClp(monthlyPricing.clp.current * 0.8)}
                                <div className="text-xs text-gray-500 mt-1">
                                  USD {formatUsd(selectedPeriod === 'anual' ? monthlyPricing.usd.current * 0.8 : pricing.usd.current, false)}
                                </div>
                                <span className="text-lg">/mes</span>
                              </div>
                              <div className="text-sm text-blue-400">
                                {formatClp(pricing.clp.current)}/año 
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-2xl font-bold text-green-400">
                                {formatClp(pricing.clp.current)}
                                <div className="text-xs text-gray-500 mt-1">
                                  USD {formatUsd(pricing.usd.current, false)}
                                </div>
                                <div className="text-lg">/mes </div>
                              </div>
                            </>
                          )}
                          <div className="text-sm text-blue-400 mt-1">
                            {selectedPeriod === 'mensual' ? 'Pago mensual' : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Sucursales */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors" 
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Sucursales</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center text-white font-semibold px-2">
                        {plan.limites?.sucursales === -1 ? (
                          <span className="text-green-400">Ilimitadas</span>
                        ) : (
                          plan.limites?.sucursales || '1'
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Precio - Mobile */}
                  <div className="lg:hidden mb-3">
                    <div className="bg-gray-800/50 border border-gray-700/30 rounded-xl p-4 mb-3">
                      <div className="mb-3">
                        <div className="font-medium text-gray-300 mb-1 flex items-center justify-between">
                          <span>Precio (**)</span>
                          <div className="inline-flex items-center bg-gray-700 rounded-full p-0.5 text-xs">
                            <button
                              onClick={() => setSelectedPeriod('mensual')}
                              className={`px-3 py-1 rounded-full font-medium transition-colors ${
                                selectedPeriod === 'mensual'
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-400 hover:text-white'
                              }`}
                            >
                              Mensual
                            </button>
                            <button
                              onClick={() => setSelectedPeriod('anual')}
                              className={`px-3 py-1 rounded-full font-medium transition-colors ${
                                selectedPeriod === 'anual'
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-400 hover:text-white'
                              }`}
                            >
                              Anual (-20%)
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 text-right pr-2">
                          1 USD = ${exchangeRateInfo.formatted} CLP (*)
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {planes.filter(plan => plan.price > 0).map((plan) => {
                          const pricing = getPlanPricing(plan, selectedPeriod);
                          const monthlyPricing = getPlanPricing(plan, 'mensual');
                          
                          return (
                            <div key={plan.id} className="bg-gray-700/50 rounded-lg p-2">
                              <div className="text-xs text-gray-300 mb-1 font-medium">{plan.name}</div>
                              
                              {selectedPeriod === 'anual' ? (
                                <>
                                  <div className="text-sm font-bold text-green-400">
                                    {formatClp(monthlyPricing.clp.current * 0.8)}
                                    <div className="text-[10px] text-gray-400 mt-1">
                                      USD {formatUsd(monthlyPricing.usd.current * 0.8, false)}
                                    </div>
                                    <span className="text-xs">/mes</span>
                                  </div>
                                  <div className="text-xs text-blue-400">
                                    {formatClp(pricing.clp.current)}/año
                                  </div>
                                </>
                              ) : (
                                <div>
                                  <div className="text-sm font-bold text-green-400">
                                    {formatClp(pricing.clp.current)}
                                    <div className="text-[10px] text-gray-400 mt-1">
                                      USD {formatUsd(pricing.usd.current, false)}
                                    </div>
                                    <span className="text-xs">/mes</span>
                                  </div>
                                </div>
                              )}
                              
                              <div className="text-[10px] text-blue-400 mt-1">
                                {selectedPeriod === 'mensual' ? 'Pago mensual' : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Sucursales - Mobile */}
                  <div className="lg:hidden">
                    <div className="bg-gray-800/50 border border-gray-700/30 rounded-xl p-4">
                      <div className="font-medium text-gray-300 mb-3">Sucursales</div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        {planes.filter(plan => plan.price > 0).map((plan) => (
                          <div key={plan.id} className="bg-gray-700/50 rounded-lg p-3">
                            <div className="text-xs text-gray-400 mb-1">{plan.name}</div>
                            <div className="text-white font-semibold">
                              {plan.limites?.sucursales === -1 ? (
                                <span className="text-green-400">Ilimitadas</span>
                              ) : (
                                plan.limites?.sucursales || '1'
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Usuarios */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Clientes</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center text-white font-semibold px-2">
                        {plan.limites?.usuarios === -1 ? (
                          <span className="text-green-400">Ilimitados</span>
                        ) : (
                          plan.limites?.usuarios || '2'
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="lg:hidden">
                    <div className="bg-gray-800/30 border border-gray-700/20 rounded-xl p-4">
                      <div className="font-medium text-gray-300 mb-3">Clientes</div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        {planes.filter(plan => plan.price > 0).map((plan) => (
                          <div key={plan.id} className="bg-gray-700/50 rounded-lg p-3">
                            <div className="text-xs text-gray-400 mb-1">{plan.name}</div>
                            <div className="text-white font-semibold">
                              {plan.limites?.usuarios === -1 ? (
                                <span className="text-green-400">Ilimitados</span>
                              ) : (
                                plan.limites?.usuarios || '2'
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  

                  
                  {/* Resto de características - Solo mostrar en mobile como lista simple */}
                  <div className="lg:hidden space-y-4">
                    {[
                      { label: 'Vehículos', key: 'vehiculos', default: '50' },
                      { label: 'Mantenimientos /mes', key: 'mantenimientos', default: '20' },
                      { label: 'Almacenamiento (pdf)', key: 'almacenamiento', default: '1GB', isSizeValue: true }
                    ].map(feature => (
                      <div key={feature.key} className="bg-gray-800/30 border border-gray-700/20 rounded-xl p-4">
                        <div className="font-medium text-gray-300 mb-3">{feature.label}</div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {planes.filter(plan => plan.price > 0).map((plan) => (
                            <div key={plan.id} className="bg-gray-700/50 rounded-lg p-3">
                              <div className="text-xs text-gray-400 mb-1">{plan.name}</div>
                              <div className="text-white font-semibold">
                                {feature.type === 'feature' ? (
                                  feature.plans.includes(plan.name) ? (
                                    <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                                  ) : (
                                    <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                                  )
                                ) : plan.limites?.[feature.key] === -1 ? (
                                  <span className="text-green-400">Ilimitados</span>
                                ) : feature.isSizeValue ? (
                                  `${plan.limites?.[feature.key] || feature.default.replace('GB', '')}GB`
                                ) : (
                                  plan.limites?.[feature.key] || feature.default
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {/* Características de Check/X en móvil */}
                    {[
                      'Gestión de citas',
                      'Multi-sucursal',
                      'Reportes avanzados',
                      'Catálogo de marcas y vehículos',
                      'Tienda de repuestos integrada',
                      'Recordatorios automáticos',
                      'Búsqueda por patente'
                    ].map(feature => (
                      <div key={feature} className="bg-gray-800/30 border border-gray-700/20 rounded-xl p-4">
                        <div className="font-medium text-gray-300 mb-3">{feature}</div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {planes.filter(plan => plan.price > 0).map((plan) => (
                            <div key={plan.id} className="bg-gray-700/50 rounded-lg p-3">
                              <div className="text-xs text-gray-400 mb-1">{plan.name}</div>
                              <div className="flex justify-center">
                                {feature === 'Búsqueda por patente' ? (
                                  plan.name === 'Gold' ? (
                                    <CheckIcon className="h-5 w-5 text-green-400" />
                                  ) : (
                                    <XMarkIcon className="h-5 w-5 text-red-500" />
                                  )
                                ) : ['Catálogo de marcas y vehículos', 'Tienda de repuestos integrada', 'Recordatorios automáticos', 'Reportes avanzados'].includes(feature) ? (
                                  ['Silver', 'Gold'].includes(plan.name) ? (
                                    <CheckIcon className="h-5 w-5 text-green-400" />
                                  ) : (
                                    <XMarkIcon className="h-5 w-5 text-red-500" />
                                  )
                                ) : (
                                  <CheckIcon className="h-5 w-5 text-green-400" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Características de Check/X en móvil */}
                    {[
                      'Acceso a API'
                    ].map(feature => (
                      <div key={feature} className="bg-gray-800/30 border border-gray-700/20 rounded-xl p-4">
                        <div className="font-medium text-gray-300 mb-3">{feature}</div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {planes.filter(plan => plan.price > 0).map((plan) => (
                            <div key={plan.id} className="bg-gray-700/50 rounded-lg p-3">
                              <div className="text-xs text-gray-400 mb-1">{plan.name}</div>
                              <div className="flex justify-center">
                                {(plan.id === 'revvly_bronze' || plan.id === 'revvly_silver') ? (
                                  <XMarkIcon className="h-5 w-5 text-red-500" />
                                ) : (
                                  <CheckIcon className="h-5 w-5 text-green-400" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: Mantener filas originales pero ocultar en mobile */}
                  {/* Vehículos */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Vehículos</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center text-white font-semibold px-2">
                        {plan.limites?.vehiculos === -1 ? (
                          <span className="text-green-400">Ilimitados</span>
                        ) : (
                          plan.limites?.vehiculos || '50'
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Mantenimientos */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Mantenimientos /mes</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center text-white font-semibold px-2">
                        {plan.limites?.mantenimientos === -1 ? (
                          <span className="text-green-400">Ilimitados</span>
                        ) : (
                          plan.limites?.mantenimientos || '20'
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Almacenamiento */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Almacenamiento (pdf)</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center text-white font-semibold px-2">
                        {plan.limites?.almacenamiento ? `${plan.limites.almacenamiento}GB` : '1GB'}
                      </div>
                    ))}
                  </div>
                  
                  {/* Características con checkmarks */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Gestión de citas</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                      </div>
                    ))}
                  </div>
                  
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Multi-sucursal</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                      </div>
                    ))}
                  </div>

                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Reportes avanzados</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {plan.id !== 'revvly_bronze' ? (
                          <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                        ) : (
                          <XMarkIcon className="h-6 w-6 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Catálogo de marcas y vehículos</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {['Silver', 'Gold'].includes(plan.name) ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Tienda de repuestos integrada</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {['Silver', 'Gold'].includes(plan.name) ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Recordatorios automáticos</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {['Silver', 'Gold'].includes(plan.name) ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Características adicionales - Desktop - Moved to end */}
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/30 border border-gray-700/20 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Búsqueda por patente</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {plan.name === 'Gold' ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="hidden lg:grid gap-4 py-4 px-4 rounded-xl bg-gray-800/50 border border-gray-700/30 hover:bg-gray-700/30 transition-colors"
                       style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(3, 1fr)' }}>
                    <div className="font-medium text-gray-300 px-2">Acceso a API</div>
                    {planes.filter(plan => plan.price > 0).map((plan) => (
                      <div key={plan.id} className="text-center px-2">
                        {plan.id === 'revvly_gold' ? (
                          <CheckIcon className="h-6 w-6 text-green-400 mx-auto drop-shadow-lg" />
                        ) : (
                          <XMarkIcon className="h-6 w-6 text-red-500 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
        )}

      {/* Información adicional */}
      <div className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700/50 mt-6">
        <h3 className="text-xl font-bold text-white text-center mb-6">
          Todas las suscripciones incluyen
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: ShieldCheckIcon,
              title: 'Seguridad',
              desc: 'Datos encriptados y respaldos automáticos'
            },
            {
              icon: BoltIcon,
              title: 'Rendimiento',
              desc: 'Alta disponibilidad y carga rápida'
            },
            {
              icon: CurrencyDollarIcon,
              title: 'Sin sorpresas',
              desc: 'Precios claros sin cargos ocultos'
            },
            {
              icon: CheckIcon,
              title: 'Soporte',
              desc: 'Atención prioritaria cuando la necesites'
            }
          ].map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="bg-blue-500/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <item.icon className="h-6 w-6 text-blue-400" />
              </div>
              <h4 className="text-base font-semibold text-white mb-2">{item.title}</h4>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Exchange rate explanations */}
      <div className="mt-8 text-center text-gray-400 text-sm">
        <div className="mb-1">
          ( * ) : Tipo de cambio obtenido del Banco Central (Dólar Observado diario).
        </div>
        <div>
          ( ** ) : El valor mensual / anual puede variar según la fluctuación del tipo de cambio.
        </div>
      </div>
    </div>
  );
};

export default PricingPage;